import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { request, type EndorsedOutResponse, type EndorseResult } from './client';

export { isEndpointMissing } from './conversation';

/**
 * Endorsing a participant from one study to another.
 *
 * Landed on the API on 25 September 2026 (migration 035), so the shapes are
 * the generated ones; docs/api-contract-endorsements.md is the record of the
 * contract. `isEndpointMissing` is re-exported for a deployment pointed at an
 * API older than these routes, where the board keeps both endorsement columns
 * in their Preview state instead of failing.
 *
 *   POST /submissions/{id}/endorse          submissions:update
 *        { study } → 201 { endorsement }; the row moves to that study at
 *        `endorsed` — its board's Newly endorsed column
 *   GET  /submissions/endorsements?from=    submissions:read
 *        → { endorsements, total, limit } — a board's Endorsed out column
 *
 * Rows carry `endorsedFrom` (the study that handed them over, and when), and
 * `/meta/` carries `endorseTargets`, every study someone may be endorsed to.
 */

/** `enabled: false` against an older API, so the board does not collect a 404 per render. */
export const useEndorsedOut = (study: string, enabled = true) =>
  useQuery({
    queryKey: ['endorsed-out', study],
    queryFn: () =>
      request<EndorsedOutResponse>('/submissions/endorsements', { query: { from: study } }),
    enabled,
  });

export function useEndorse() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, study }: { id: string; study: string }) =>
      request<EndorseResult>(`/submissions/${id}/endorse`, { method: 'POST', body: { study } }),
    onSuccess: (_data, { id }) => {
      // The row changes board and status, both boards' columns change, and
      // the Overview counts move with the status.
      void qc.invalidateQueries({ queryKey: ['submissions'] });
      void qc.invalidateQueries({ queryKey: ['submission', id] });
      void qc.invalidateQueries({ queryKey: ['endorsed-out'] });
      void qc.invalidateQueries({ queryKey: ['overview'] });
    },
  });
}
